import { useQueryClient, focusManager } from "@tanstack/react-query";
import {
	signOut as amplifySignOut,
	getCurrentUser as getCurrentCognitoUser,
	AuthUser,
	signIn as amplifySignIn,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import * as React from "react";
import {
	clearSignInOrigin,
	getPostSignOutRedirectPath,
} from "~utils/functions/get-signout-redirect-path";
import { useGetAuthAccount } from "~auth/queries/useGetAuthAccount";
import {
	GetAuthAccountQuery,
	GetAuthUserQuery,
} from "~auth/types/__generated/gql/graphql";
import { useGetAuthUser } from "~auth/queries/useGetAuthUser";

export interface AuthContextValue {
	isLoading: boolean;
	isAuthenticated: boolean;
	authCognitoUser: AuthUser | undefined;
	authUser: GqlResultOf<GetAuthUserQuery> | null | undefined;
	authAccount: GqlResultOf<GetAuthAccountQuery> | null | undefined;
	signOut: () => Promise<string>;
	replaceSession: (input: {
		username: string;
		password: string;
		global?: boolean;
		authFlowType?:
			| "USER_SRP_AUTH"
			| "USER_PASSWORD_AUTH"
			| "CUSTOM_WITHOUT_SRP";
	}) => Promise<
		Pick<AuthContextValue, "authCognitoUser" | "authUser" | "authAccount">
	>;
	refreshSession: () => Promise<
		Pick<AuthContextValue, "authCognitoUser" | "authUser" | "authAccount">
	>;
}

export const AuthContext = React.createContext<AuthContextValue>({
	isLoading: true,
	isAuthenticated: false,
	authCognitoUser: undefined,
	authUser: undefined,
	authAccount: undefined,
	signOut: () => Promise.resolve(getPostSignOutRedirectPath()),
	replaceSession: () =>
		Promise.resolve({
			authCognitoUser: undefined,
			authUser: undefined,
			authAccount: undefined,
		}),
	refreshSession: () =>
		Promise.resolve({
			authCognitoUser: undefined,
			authUser: undefined,
			authAccount: undefined,
		}),
});

export interface AuthProviderProps {
	children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const queryClient = useQueryClient();
	const isReplacingSessionRef = React.useRef(false);
	const refreshSessionRef = React.useRef<
		| (() => Promise<
				Pick<AuthContextValue, "authCognitoUser" | "authUser" | "authAccount">
		  >)
		| null
	>(null);

	// eslint-disable-next-line @typescript-eslint/no-deprecated
	const { refetch: refetchAuthUser, data: gqlAuthUser } = useGetAuthUser();
	const { refetch: refetchAuthAccount, data: gqlAuthAccount } =
		// eslint-disable-next-line @typescript-eslint/no-deprecated
		useGetAuthAccount();
	const [state, setState] = React.useState<
		Pick<
			AuthContextValue,
			"isLoading" | "authCognitoUser" | "authUser" | "authAccount"
		>
	>({
		isLoading: true,
		authCognitoUser: undefined,
		authUser: undefined,
		authAccount: undefined,
	});
	const setPartialState = React.useCallback(
		(partial: Partial<typeof state>) => {
			setState((prev) => ({
				...prev,
				...partial,
			}));
		},
		[],
	);
	const clearSessionHint = React.useCallback(async () => {
		if (typeof window === "undefined") return;

		await fetch("/api/session-hint", {
			method: "DELETE",
			credentials: "same-origin",
		}).catch(() => undefined);
	}, []);

	React.useEffect(() => {
		async function initialCheck() {
			setPartialState({ isLoading: true });

			const [authCognitoUser, authUser, authAccount] = await fetchAllAuthData();

			setPartialState({
				isLoading: false,
				authCognitoUser,
				authUser,
				authAccount,
			});
		}

		void initialCheck();

		const unsubscribe = focusManager.subscribe((isVisible) => {
			if (isVisible) void refreshSession();
		});

		return () => {
			unsubscribe();
		};
	}, [setPartialState]);

	React.useEffect(() => {
		setPartialState({
			authAccount: gqlAuthAccount,
			authUser: gqlAuthUser,
		});
	}, [gqlAuthAccount, gqlAuthUser]);

	const fetchAllAuthData = async () => {
		return Promise.all([
			getCurrentCognitoUser().catch(() => undefined),
			refetchAuthUser()
				.then((r) => r.data)
				.catch(() => undefined),
			refetchAuthAccount()
				.then((r) => r.data)
				.catch(() => undefined),
		]);
	};

	const sleep = (ms: number) =>
		new Promise<void>((resolve) => {
			globalThis.setTimeout(resolve, ms);
		});

	const waitForSignedOut = async () => {
		for (let attempt = 0; attempt < 10; attempt += 1) {
			const currentUser = await getCurrentCognitoUser().catch(() => undefined);
			if (!currentUser) return;
			await sleep(100);
		}
	};

	const waitForAuthenticatedSession = async () => {
		for (let attempt = 0; attempt < 20; attempt += 1) {
			const [authCognitoUser, authUser, authAccount] = await fetchAllAuthData();

			if (authCognitoUser && authUser && authAccount) {
				setPartialState({
					isLoading: false,
					authCognitoUser,
					authUser,
					authAccount,
				});

				return {
					authCognitoUser,
					authUser,
					authAccount,
				};
			}

			await sleep(250);
		}

		throw new Error("Unable to restore authenticated session.");
	};

	const refreshSession = async () => {
		const [cognitoUser, authUser, authAccount] = await fetchAllAuthData();

		setPartialState({
			authCognitoUser: cognitoUser,
			authUser,
			authAccount,
		});

		return {
			authCognitoUser: cognitoUser,
			authUser,
			authAccount,
		};
	};
	refreshSessionRef.current = refreshSession;

	const replaceSession: AuthContextValue["replaceSession"] = async ({
		username,
		password,
		global = false,
		authFlowType = "USER_PASSWORD_AUTH",
	}) => {
		isReplacingSessionRef.current = true;
		setPartialState({ isLoading: true });

		try {
			await amplifySignOut({ global });
			await waitForSignedOut();

			await amplifySignIn({
				username,
				password,
				options: {
					authFlowType,
				},
			});

			return await waitForAuthenticatedSession();
		} finally {
			isReplacingSessionRef.current = false;
			setPartialState({ isLoading: false });
		}
	};

	React.useEffect(() => {
		const cancel = Hub.listen("auth", ({ payload }) => {
			const event = payload.event;
			if (event === "signedIn" || event === "tokenRefresh") {
				void refreshSessionRef.current?.();
			}
			if (event === "tokenRefresh_failure") {
				void refreshSessionRef.current?.();
			}
			if (event === "signedOut") {
				void (async () => {
					const currentUser = await getCurrentCognitoUser().catch(
						() => undefined,
					);

					// Ignore stale signedOut events that arrive after replacement sign-in wins.
					if (currentUser) return;

					if (!isReplacingSessionRef.current) {
						queryClient.clear();
					}

					setPartialState({
						isLoading: isReplacingSessionRef.current,
						authCognitoUser: undefined,
						authUser: undefined,
						authAccount: undefined,
					});
					await clearSessionHint();
					if (typeof window !== "undefined") {
						clearSignInOrigin();
						window.localStorage.removeItem("managed-session-active");
						window.dispatchEvent(new Event("managed-session-state-changed"));
					}
				})();
			}
		});

		return () => {
			cancel();
		};
	}, [clearSessionHint, queryClient, setPartialState]);

	const signOut = async () => {
		const redirectPath = getPostSignOutRedirectPath();
		await clearSessionHint();
		await amplifySignOut();
		if (typeof window !== "undefined") {
			clearSignInOrigin();
			window.localStorage.removeItem("managed-session-active");
			window.dispatchEvent(new Event("managed-session-state-changed"));
		}
		queryClient.clear();
		setPartialState({
			isLoading: false,
			authCognitoUser: undefined,
		});

		return redirectPath;
	};

	return (
		<AuthContext.Provider
			value={{
				...state,
				isAuthenticated:
					!!state.authCognitoUser && !!state.authUser && !!state.authAccount,
				signOut,
				replaceSession,
				refreshSession,
			}}>
			{children}
		</AuthContext.Provider>
	);
};

export const AuthConsumer = AuthContext.Consumer;

export const useAuthContext = () => React.useContext(AuthContext);
